import { useEffect } from "react";
import {
  LDSButton,
  LDSDropDown,
  LDSHeading,
  LDSIconTextField,
  LDSInputTextArea,
  LDSMessages,
  LDSOrderList,
  LDSSteps,
} from "@resmed/lumina-design-system";
import Icons from "@resmed/lumina-design-system-assets/dist/icons";
import ParticipantListItem from "../../components/ParticipantListItem";
import ActionConfirmation from "../../components/ActionConfirmation";
import { SkeletonDropdown, SkeletonParticipantList } from "../../components/skeleton";
import { useChatSidebarHeader } from "../../components/ChatSidebar/ChatSidebar";
import { CREATE_CHAT_STRINGS } from "./constants";
import { useCreateChatViewModel } from "./CreateChatViewModel";
import type { StaffParticipant } from "../../features/chat/types";
import usePaginatedScroll from "../../hooks/usePaginatedScroll";

const CreateChat = () => {
  const vm = useCreateChatViewModel();
  const { setHeaderOverride } = useChatSidebarHeader();
  const { containerRef, rememberScrollPosition } = usePaginatedScroll(
    vm.participants.length,
    vm.userSearch,
  );

  useEffect(() => {
    setHeaderOverride({
      title: CREATE_CHAT_STRINGS.title,
      onClose: vm.onCancel,
    });

    return () => setHeaderOverride(null);
  }, [setHeaderOverride, vm.onCancel]);

  const handleParticipantScroll = (e: React.UIEvent<HTMLDivElement>) => {
    rememberScrollPosition(e);
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 150) {
      vm.onLoadMoreParticipants();
    }
  };

  return (
    <div className="lds-flex lds-flex-col lds-h-full lds-min-h-0 lds-bg-white">
      {/* Stepper */}
      <div className="lds-shrink-0 lds-px-6 lds-pt-5 lds-pb-2">
        <LDSSteps model={vm.stepsModel} activeIndex={vm.step} readOnly />
      </div>

      {/* Step content — outer shell does not scroll; inner areas control their own overflow */}
      <div className="lds-flex-1 lds-min-h-0 lds-flex lds-flex-col">
        {vm.step === 0 ? (
          /* ── Step 1: Add Participants ──────────────────────────────── */
          <>
            {/* Facility + search — fixed, never scroll away */}
            <div className="lds-shrink-0 lds-flex lds-flex-col lds-gap-4 lds-px-6 lds-pt-4 lds-pb-3">
              <div className="lds-w-full md:lds-w-[48%]">
                {vm.facilitiesLoading ? (
                  <SkeletonDropdown />
                ) : (
                  <LDSDropDown
                    id="create-chat-facility"
                    label="Facility"
                    placeholder="Select facility..."
                    options={vm.facilities}
                    optionLabel="label"
                    optionValue="value"
                    value={vm.facility}
                    onChange={(e) => vm.onFacilityChange(e.value as string)}
                    size="medium"
                    showClear={false}
                  />
                )}
              </div>

              <div className="lds-w-full md:lds-w-[48%]">
                <LDSIconTextField
                  icon={Icons.FaMagnifyingGlass}
                  iconPosition="left"
                  placeholder="Search participants..."
                  size="medium"
                  value={vm.userSearch}
                  onChange={(e) => vm.onUserSearchChange(e.target.value)}
                  disabled={vm.facilitiesLoading}
                  containerClassName="lds-w-full"
                />
              </div>
            </div>

            {/* Participant list — scrollable, triggers next page near bottom */}
            <div
              ref={containerRef}
              className="lds-flex-1 lds-overflow-y-auto lds-px-6 lds-pb-4"
              onScroll={handleParticipantScroll}
            >
              {vm.participantsLoading ? (
                <SkeletonParticipantList />
              ) : vm.participants.length > 0 ? (
                <LDSOrderList
                  value={vm.participants}
                  dataKey="ssoId"
                  controls={false}
                  dragdrop={false}
                  ariaLabel="Select participants"
                  rootClassName="create-chat-participant-list lds-w-full"
                  itemTemplate={(participant: StaffParticipant) => (
                    <ParticipantListItem
                      displayName={participant.displayName}
                      roleName={participant.role}
                      avatar={null}
                      showCheckbox
                      checked={vm.selectedSsoIds.includes(participant.ssoId)}
                      onToggle={() => vm.onToggleSsoId(participant.ssoId)}
                      borderless
                      inlineDetails
                    />
                  )}
                />
              ) : (
                <LDSHeading
                  size="h5"
                  className="lds-font-normal lds-text-sm lds-text-textcolor-caption lds-py-4 lds-text-center"
                >
                  No participants found.
                </LDSHeading>
              )}
              {vm.isFetchingNextPage && (
                <SkeletonParticipantList count={3} />
              )}

              {vm.selectedSsoIds.length > 0 && (
                <LDSHeading size="h5" className="lds-text-sm lds-text-textcolor-caption lds-mt-2">
                  {vm.selectedSsoIds.length} participant
                  {vm.selectedSsoIds.length !== 1 ? "s" : ""} selected
                </LDSHeading>
              )}
            </div>
          </>
        ) : (
          /* ── Step 2: Add Group Details ─────────────────────────────── */
          <div className="lds-flex-1 lds-overflow-y-auto lds-px-6 lds-py-4 lds-flex lds-flex-col lds-gap-4">
            <div className="lds-flex lds-flex-col lds-gap-3 lds-flex-1">
              <LDSHeading
                size="h4"
                className="lds-font-semibold lds-text-textcolor-default"
              >
                {CREATE_CHAT_STRINGS.initialMessageLabel}
              </LDSHeading>
              <LDSInputTextArea
                id="create-chat-initial-message"
                placeholder={CREATE_CHAT_STRINGS.initialMessagePlaceholder}
                value={vm.groupName}
                onChange={(e) => vm.onGroupNameChange(e.target.value)}
                rows={10}
                rootClassName="lds-w-full"
              />
            </div>

            {/* Selected participants summary */}
            {/* <div className="lds-rounded-lg lds-border lds-border-bordercolor-default lds-bg-white">
              <div className="lds-flex lds-items-center lds-px-4 lds-py-3 lds-border-b lds-border-bordercolor-default">
                <LDSHeading size="h5" className="lds-flex-1 lds-font-semibold lds-text-textcolor-default">
                  Participants
                </LDSHeading>
                <LDSButton
                  id="edit-participants-btn"
                  label="Edit"
                  icon={Icons.FaPencil}
                  severity="default"
                  outlined
                  size="medium"
                  onClick={vm.onBack}
                />
              </div>
              <div className="lds-p-3 lds-space-y-2">
                {vm.selectedParticipants.map((p) => (
                  <ParticipantListItem
                    key={p.ssoId}
                    displayName={p.displayName}
                    roleName={p.role}
                    avatar={null}
                  />
                ))}
              </div>
            </div> */}
          </div>
        )}
      </div>

      {/* Footer — primary action first, then secondary, all left-aligned */}
      <div className="lds-flex lds-items-center lds-gap-2 lds-shrink-0 lds-px-6 lds-py-4 lds-border-t lds-border-bordercolor-weak">
        {vm.step === 0 ? (
          <LDSButton
            id="create-chat-next"
            label="Next"
            disabled={!vm.canGoNext || vm.isValidating}
            loading={vm.isValidating}
            onClick={vm.onNext}
          />
        ) : (
          <LDSButton
            id="create-chat-create"
            label={CREATE_CHAT_STRINGS.createChatLabel}
            disabled={!vm.canCreate || vm.isCreating}
            loading={vm.isCreating}
            onClick={vm.onCreate}
          />
        )}
        <LDSButton
          id="create-chat-cancel"
          label="Cancel"
          severity="secondary"
          outlined
          onClick={vm.onCancel}
        />
      </div>

      <ActionConfirmation
        visible={vm.facilityChangeConfirmVisible}
        title={CREATE_CHAT_STRINGS.changeFacilityTitle}
        confirmLabel={CREATE_CHAT_STRINGS.changeFacilityConfirmLabel}
        confirmSeverity="danger"
        onConfirm={vm.onConfirmFacilityChange}
        onCancel={vm.onCancelFacilityChange}
      />

      {vm.createError && (
        <div className="lds-fixed lds-bottom-20 lds-left-4 lds-right-4 lds-z-50">
          <LDSMessages
            id="create-chat-error"
            severity="danger"
            detail={vm.createError}
            sticky
            closable={false}
          />
        </div>
      )}

      {/* Rejected participants dialog */}
      {vm.rejectedDialogVisible && (
        <div
          className="lds-fixed lds-inset-0 lds-z-50 lds-flex lds-items-center lds-justify-center"
          role="dialog"
          aria-modal="true"
        >
          <div className="lds-absolute lds-inset-0 lds-bg-black/40" />
          <div className="lds-relative lds-bg-white lds-rounded-lg lds-shadow-xl lds-w-full lds-max-w-sm lds-mx-4 lds-flex lds-flex-col lds-gap-4 lds-p-6">
            <LDSMessages
              id="rejected-participants-warning"
              severity="warning"
              detail={CREATE_CHAT_STRINGS.rejectedParticipantsWarning}
              sticky
              subtle
              closable={false}
            />

            <div className="lds-flex lds-flex-col lds-gap-2">
              <LDSHeading
                size="h5"
                className="lds-text-xs lds-font-semibold lds-text-textcolor-caption lds-uppercase lds-tracking-wide"
              >
                Participants
              </LDSHeading>
              <div className="lds-max-h-48 lds-overflow-y-auto lds-space-y-2">
                {vm.rejectedParticipants.map((p) => (
                  <ParticipantListItem
                    key={p.ssoId}
                    displayName={p.displayName}
                    roleName={p.role}
                    avatar={null}
                  />
                ))}
              </div>
            </div>

            <div className="lds-flex lds-flex-col lds-gap-2">
              <LDSButton
                id="proceed-with-others-btn"
                label={CREATE_CHAT_STRINGS.proceedWithOthers}
                disabled={!vm.canProceedWithOthers}
                onClick={vm.onProceedWithOthers}
              />
              <LDSButton
                id="rejected-cancel-btn"
                label="Cancel"
                severity="secondary"
                outlined
                onClick={vm.onCancelRejected}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateChat;
